import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { collection, onSnapshot, query, orderBy, addDoc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import { subscribeToJHA } from '../../services/jhaService'
import AppLayout from '../../components/layout/AppLayout'

const RISK_COLORS: Record<string, string> = { Low: '#16a34a', Medium: '#d97706', High: '#dc2626', Critical: '#dc2626' }
const STATUS_COLORS: Record<string, string> = { draft: '#6b7280', active: '#16a34a', closed: '#1a3c5e' }
const STATUS_ORDER = ['draft', 'active', 'closed']
const STATUS_LABELS: Record<string, string> = { draft: 'Draft', active: 'Active', closed: 'Closed' }

interface Comment {
  id: string
  author: string
  authorId: string
  text: string
  createdAt: string
}

export default function JHADetailPage() {
  const { id } = useParams<{ id: string }>()
  const { orgId, userData } = useAuth()
  const navigate = useNavigate()

  const [jha, setJha] = useState<any>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [addingComment, setAddingComment] = useState(false)
  const [showAddCrew, setShowAddCrew] = useState(false)
  const [showAddVisitor, setShowAddVisitor] = useState(false)
  const [crewName, setCrewName] = useState('')
  const [crewCompany, setCrewCompany] = useState('')
  const [visitorName, setVisitorName] = useState('')
  const [visitorCompany, setVisitorCompany] = useState('')
  const [visitorAgreed, setVisitorAgreed] = useState(false)
  const [expandedHazards, setExpandedHazards] = useState(false)

  // Subscribe to JHA
  useEffect(() => {
    if (!id) return
    const unsub = subscribeToJHA(orgId || 'dev-org-1', id, data => {
      setJha(data)
      setLoading(false)
    })
    return () => unsub()
  }, [id, orgId])

  // Subscribe to comments
  useEffect(() => {
    if (!id) return
    const q = query(
      collection(db, 'organizations', orgId || 'dev-org-1', 'jha', id, 'comments'),
      orderBy('createdAt', 'asc')
    )
    const unsub = onSnapshot(q, snap => {
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() } as Comment)))
    })
    return () => unsub()
  }, [id, orgId])

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

  const canTransitionTo = (target: string): boolean => {
    if (!jha) return false
    const idx = STATUS_ORDER.indexOf(jha.status)
    const targetIdx = STATUS_ORDER.indexOf(target)
    return targetIdx === idx + 1
  }

  const handleStatusUpdate = async (newStatus: string) => {
    if (!jha || !canTransitionTo(newStatus)) return
    const { updateJHAStatus } = await import('../../services/jhaService')
    await updateJHAStatus(orgId!, id!, newStatus)
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !orgId || !id) return
    setAddingComment(true)
    try {
      await addDoc(collection(db, 'organizations', orgId, 'jha', id, 'comments'), {
        author: userData?.name || userData?.email || 'Unknown',
        authorId: userData?.uid || 'unknown',
        text: newComment.trim(),
        createdAt: new Date().toISOString()
      })
      setNewComment('')
    } catch (e) {
      // silent
    } finally {
      setAddingComment(false)
    }
  }

  const handleAddCrew = async () => {
    if (!crewName.trim()) return
    const { addCrewSignature } = await import('../../services/jhaService')
    await addCrewSignature(orgId!, id!, {
      userId: userData?.uid || 'crew-' + Date.now(),
      displayName: crewName.trim(),
      role: 'crew',
      company: crewCompany.trim() || 'SiteDeck Corp',
      pinVerified: true
    })
    setShowAddCrew(false)
    setCrewName('')
    setCrewCompany('')
  }

  const handleAddVisitor = async () => {
    if (!visitorName.trim() || !visitorAgreed) return
    const { addVisitorSignature } = await import('../../services/jhaService')
    await addVisitorSignature(orgId!, id!, {
      visitorName: visitorName.trim(),
      company: visitorCompany.trim() || 'Visitor'
    })
    setShowAddVisitor(false)
    setVisitorName('')
    setVisitorCompany('')
    setVisitorAgreed(false)
  }

  const handleEmail = () => {
    if (!jha) return
    const subject = encodeURIComponent(`JHA: ${jha.taskDescription}`)
    const body = encodeURIComponent(
      `Project: ${jha.projectName}\nLocation: ${jha.jobLocation}\nSupervisor: ${jha.supervisorName}\nDate: ${jha.date}\n\nHazards:\n${jha.hazards?.map((h: any, i: number) => `${i + 1}. ${h.taskStep}\n   Hazard: ${h.potentialHazard}\n   Control: ${h.controlMeasure}\n   Risk: ${h.riskLevel}`).join('\n\n')}\n\nPPE Required: ${jha.ppeRequired?.join(', ')}\n\nEmergency: ${jha.emergencyHospital}\nContact: ${jha.emergencyContact}\nFirst Aid: ${jha.firstAidLocation}\n\nView in SiteDeck Pro: ${window.location.origin}/jha/${id}`
    )
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
  }

  const currentStatusIdx = jha ? STATUS_ORDER.indexOf(jha.status) : 0

  if (loading) {
    return (
      <AppLayout title="JHA Detail">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      </AppLayout>
    )
  }

  if (!jha) {
    return (
      <AppLayout title="JHA Detail">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <p className="text-lg font-medium">JHA not found</p>
          <button onClick={() => navigate('/jha')} className="mt-4 text-blue-600 hover:underline text-sm">
            ← Back to JHA List
          </button>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="JHA Detail">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/jha')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          ← Back to JHA List
        </button>
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          ✉ Send by Email
        </button>
      </div>

      <div className="max-w-5xl flex gap-6">
        {/* Left column */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Header card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <h1 className="text-xl font-bold text-gray-900 flex-1">{jha.taskDescription}</h1>
              <span
                className="px-3 py-1 rounded text-xs font-semibold text-white"
                style={{ backgroundColor: STATUS_COLORS[jha.status] || '#888' }}
              >
                {STATUS_LABELS[jha.status] || jha.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block text-xs uppercase tracking-wide">Project</span>
                <span className="text-gray-800 font-medium">{jha.projectName}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs uppercase tracking-wide">Location</span>
                <span className="text-gray-800 font-medium">{jha.jobLocation}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs uppercase tracking-wide">Supervisor</span>
                <span className="text-gray-800 font-medium">{jha.supervisorName}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs uppercase tracking-wide">Date</span>
                <span className="text-gray-800 font-medium">{jha.date ? new Date(jha.date).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Hazard table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                Hazard Analysis — {jha.hazards?.length || 0} Steps
              </h3>
              <button
                onClick={() => setExpandedHazards(!expandedHazards)}
                className="text-xs text-blue-600 hover:underline font-medium"
              >
                {expandedHazards ? 'Collapse' : 'Expand'} All
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-2 font-medium text-gray-600 w-8">#</th>
                  <th className="text-left px-5 py-2 font-medium text-gray-600">Task / Work Step</th>
                  <th className="text-left px-5 py-2 font-medium text-gray-600">Potential Hazard</th>
                  <th className="text-left px-5 py-2 font-medium text-gray-600">Control Measure</th>
                  <th className="text-left px-5 py-2 font-medium text-gray-600 w-20">Risk</th>
                </tr>
              </thead>
              <tbody>
                {jha.hazards?.map((h: any, i: number) => (
                  <tr key={h.id} className="border-b border-gray-100">
                    <td className="px-5 py-3 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-5 py-3 text-gray-800 text-xs">{h.taskStep}</td>
                    <td className="px-5 py-3 text-gray-600 text-xs">{h.potentialHazard}</td>
                    <td className="px-5 py-3 text-gray-600 text-xs">{h.controlMeasure}</td>
                    <td className="px-5 py-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold text-white"
                        style={{ backgroundColor: RISK_COLORS[h.riskLevel] || '#888' }}
                      >
                        {h.riskLevel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PPE Grid */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">PPE Required</h3>
            <div className="grid grid-cols-3 gap-2">
              {jha.ppeRequired?.map((ppe: string) => (
                <div key={ppe} className="flex items-center gap-2 text-sm text-gray-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-green-600">✓</span> {ppe}
                </div>
              ))}
            </div>
          </div>

          {/* Emergency Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Emergency Information</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block text-xs">Nearest Hospital</span>
                <span className="text-gray-800 font-medium">{jha.emergencyHospital}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Emergency Contact</span>
                <span className="text-gray-800 font-medium">{jha.emergencyContact}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">First Aid Location</span>
                <span className="text-gray-800 font-medium">{jha.firstAidLocation}</span>
              </div>
            </div>
          </div>

          {/* Status Update */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Update Status</h3>
            <div className="flex items-center gap-2 mb-4">
              {STATUS_ORDER.map((st, idx) => {
                const isDone = idx < currentStatusIdx
                const isCurrent = idx === currentStatusIdx
                const isNext = idx === currentStatusIdx + 1
                return (
                  <React.Fragment key={st}>
                    <button
                      onClick={() => {
                        if (!canTransitionTo(st)) return
                        if (st === 'active') handleStatusUpdate('active')
                        else if (st === 'closed') handleStatusUpdate('closed')
                      }}
                      disabled={!canTransitionTo(st)}
                      className={`flex-1 py-2.5 rounded-lg font-medium text-sm border-2 transition-all ${
                        isDone || isCurrent ? 'text-white' : 'opacity-40'
                      }`}
                      style={{
                        borderColor: STATUS_COLORS[st],
                        backgroundColor: isDone || isCurrent ? STATUS_COLORS[st] : 'transparent',
                        color: isDone || isCurrent ? '#fff' : STATUS_COLORS[st],
                        cursor: canTransitionTo(st) ? 'pointer' : 'default'
                      }}
                    >
                      {STATUS_LABELS[st]}
                    </button>
                    {idx < STATUS_ORDER.length - 1 && <span className="text-gray-300 text-lg">→</span>}
                  </React.Fragment>
                )
              })}
            </div>
            <p className="text-xs text-gray-400">
              {currentStatusIdx === STATUS_ORDER.length - 1
                ? 'JHA is fully closed.'
                : `Next step: ${STATUS_LABELS[STATUS_ORDER[currentStatusIdx + 1]]}`}
            </p>
          </div>
        </div>

        {/* Right column — Crew, Visitors, Comments */}
        <div className="w-80 flex-shrink-0 space-y-5">

          {/* Crew Sign-Off */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Crew</h3>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {jha.crewSignatures?.length || 0} signed
              </span>
            </div>
            <div className="px-5 py-4 space-y-3">
              {jha.crewSignatures?.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No crew members signed yet.</p>
              ) : (
                jha.crewSignatures.map((s: any) => (
                  <div key={s.userId} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-sm font-bold">
                      {s.displayName[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{s.displayName}</p>
                      <p className="text-xs text-gray-400">{s.company} • {s.role}</p>
                    </div>
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                ))
              )}
              <button
                onClick={() => setShowAddCrew(!showAddCrew)}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg py-2 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                + Add Crew Sign-Off
              </button>
              {showAddCrew && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={crewName}
                    onChange={e => setCrewName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={crewCompany}
                    onChange={e => setCrewCompany(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleAddCrew}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Visitor Sign-In */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Visitors</h3>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {jha.visitorSignatures?.length || 0} signed
              </span>
            </div>
            <div className="px-5 py-4 space-y-3">
              {jha.visitorSignatures?.map((v: any) => (
                <div key={v.visitorName + v.acknowledgedAt} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-bold">
                    {v.visitorName[0]}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{v.visitorName}</p>
                    <p className="text-xs text-gray-400">{v.company}</p>
                  </div>
                  <span className="text-blue-600 text-sm">✓</span>
                </div>
              ))}
              {jha.visitorSignatures?.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">No visitors acknowledged yet.</p>
              )}

              {/* Three tiers stub */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-2 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Visitor Sign-In Options</p>
                <button disabled className="w-full bg-gray-100 text-gray-400 py-2 rounded-lg text-xs font-medium cursor-not-allowed">
                  🔲 SiteDeck User (coming soon)
                </button>
                <button disabled className="w-full bg-gray-100 text-gray-400 py-2 rounded-lg text-xs font-medium cursor-not-allowed">
                  📱 QR Code Sign-In (coming soon)
                </button>
                <button
                  onClick={() => setShowAddVisitor(!showAddVisitor)}
                  className="w-full bg-blue-50 text-blue-600 py-2 rounded-lg text-xs font-medium border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  ✏️ Foreman-Assisted Sign-In
                </button>
              </div>

              {showAddVisitor && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  <input
                    type="text"
                    placeholder="Visitor Full Name"
                    value={visitorName}
                    onChange={e => setVisitorName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={visitorCompany}
                    onChange={e => setVisitorCompany(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-gray-600 italic">
                      "I acknowledge that I have been informed of the hazards associated with this work area and will follow all safety controls described in this JHA."
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={visitorAgreed}
                      onChange={e => setVisitorAgreed(e.target.checked)}
                      className="rounded"
                    />
                    I agree to the above statement
                  </label>
                  <button
                    onClick={handleAddVisitor}
                    disabled={!visitorName.trim() || !visitorAgreed}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    Sign In Visitor
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ height: '400px' }}>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Comments</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {comments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center pt-8">No comments yet.</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">{c.author}</span>
                      <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{c.text}</p>
                  </div>
                ))
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 space-y-3">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                rows={2}
              />
              <button
                onClick={handleAddComment}
                disabled={addingComment || !newComment.trim()}
                className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
              >
                Post Comment
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}